import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import { Chip } from "@mui/material";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "@accredo/fe-shared/components/tables/hooks/useMultiTableData.jsx";

const TABLE_KEY = "contract_objects";

const STATUS_VARIANT = {
  aktiv: "contract-status_aktiv",
  abgegangen: "contract-status_archiviert",
  archiviert: "contract-status_archiviert",
};

/**
 * The Objekte of one Vertrag the user may see. A row click hands the Objekt to
 * `onSelectObjekt`; the customer's own Objekt number gets a column only for a
 * customer with an `object_ref_num_label`, headed with that label.
 */
export default function ContractObjectsTable({ vertragId, onSelectObjekt }) {
  const { enqueueSnackbar } = useSnackbar();

  const tablesConfig = useMemo(
    () => [
      {
        key: TABLE_KEY,
        label: "Objekte",
        route: "/data/objekts",
        pageSize: 10,
        storageKey: "kp_table_contract_objects",
        extraParams: {
          range: "all",
          include: ["display_sparten", "kunde"],
          sort: [{ field: "bezeichnung", order: "asc" }],
          filters: { "vertrag.id": vertragId },
        },
      },
    ],
    [vertragId]
  );

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[TABLE_KEY] || {};
  const rows = tableState.rows || [];
  const referenceLabel = rows.find((row) => row.kunde?.object_ref_num_label)
    ?.kunde.object_ref_num_label;

  useEffect(() => {
    updateTableState(TABLE_KEY, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { accessorKey: "bezeichnung", header: "Bezeichnung" },
    referenceLabel && {
      accessorKey: "customer_reference_number",
      header: referenceLabel,
    },
    {
      id: "status",
      header: "Status",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Chip
          size="small"
          label={row.original.status}
          variant={STATUS_VARIANT[row.original.status]}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <DataTable
      testId="contract-objects-table"
      columns={columns}
      data={rows}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      enableTopToolbar={false}
      onChangeSorting={(sorting) => updateTableState(TABLE_KEY, { sorting })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? 10,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(TABLE_KEY, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      storageKey="kp_table_contract_objects"
      enableSorting
      enableColumnFilters={false}
      enableGlobalFilter={false}
      enableColumnHiding={false}
      onRowClick={(row) => onSelectObjekt(row)}
    />
  );
}
